import type { OrderManagementGridView } from '@/pages/OrderManagement/All/types'
import { orderStates } from '@constants/orderManagement'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'

export type OrderState = (typeof orderStates)[keyof typeof orderStates] | 'all' | 'completed'

type PageFilters = Partial<{
  state: OrderState
  assignment: 'on-demand' | 'other' // Adjust "other" based on real values
  notification_state: 'assigned' | 'staged' | 'sent' | undefined
}>

type PageOptions = {
  filters: PageFilters
  title: string
  option?: string
}

type PageConfig = Partial<Record<string, PageOptions>>

export const pageConfig: PageConfig = {
  all: {
    filters: {},
    title: 'All Orders',
  },
  all_open: {
    filters: { state: orderStates.ALLOPEN },
    title: 'Open Orders',
  },
  accepted: {
    title: 'Accepted Orders',
    filters: { state: orderStates.ACCEPTED },
  },
  assigned: {
    title: 'Assigned Orders',
    filters: { state: orderStates.ASSIGNED },
  },
  'in progress': {
    title: 'In Progress Orders',
    filters: { state: orderStates.INPROGRESS },
  },
  completed: {
    filters: { state: orderStates.COMPLETE },
    title: 'Completed Orders',
  },
  canceled: {
    filters: { state: orderStates.CANCELED },
    title: 'Canceled Orders',
  },
}

export const carrierDispatchPageConfig = {
  all_od: {
    filters: { assignment: 'on-demand' },
    title: 'Carrier Dispatch Orders',
    option: 'All',
  },
  open_od: {
    title: 'Open Carrier Dispatch Orders',
    filters: {
      assignment: 'on-demand',
      state: orderStates.ALLOPEN,
      notification_state: undefined,
    },
    option: 'Open',
  },
  assigned_od: {
    title: 'Assigned Carrier Dispatch Orders',
    filters: {
      assignment: 'on-demand',
      state: orderStates.OPEN,
      notification_state: 'assigned',
    },
    option: 'Assigned',
  },
  staged: {
    title: 'Staged Carrier Dispatch Orders',
    filters: {
      assignment: 'on-demand',
      state: orderStates.OPEN,
      notification_state: 'staged',
    },
    option: 'Staged',
  },
  sent: {
    title: 'Sent Carrier Dispatch Orders',
    filters: {
      assignment: 'on-demand',
      state: orderStates.OPEN,
      notification_state: 'sent',
    },
    option: 'Sent',
  },
  completed_od: {
    filters: {
      assignment: 'on-demand',
      state: orderStates.COMPLETE,
      notification_state: undefined,
    },
    title: 'Completed Carrier Dispatch Orders',
    option: 'Completed',
  },
  canceled_od: {
    filters: {
      assignment: 'on-demand',
      state: orderStates.CANCELED,
      notification_state: undefined,
    },
    title: 'Canceled Carrier Dispatch Orders',
    option: 'Canceled',
  },
}

export const excelStyles = [
  {
    id: 'numberType',
    numberFormat: {
      format: '0',
    },
  },
  {
    id: 'stringType',
    dataType: 'String',
  },
  {
    id: 'dateType',
    dataType: 'DateTime',
  },
]

const exportedDefaultColumns: string[] = [
  'contracts',
  'number',
  'status',
  'flags.name',
  'hauled_by',
  'market',
  'sector',
  'location_name',
  'site_name',
  'destination_name',
  'destination_street',
  'destination_city',
  'destination_state',
  'destination_postal_code',
  'origins',
  'origin_name',
  'origin_street',
  'origin_city',
  'origin_state',
  'origin_postal_code',
  'products',
  'trailer_config',
  'loading_start',
  'loading_end',
  'carrier_start',
  'carrier_end',
  'dispatch_start',
  'dispatch_end',
  'est_drop_time',
  'bol_date',
  'delivery_date',
  'delivery_time',
  'price_flag.name',
  'price_flag.reason_code',
  'price_flag.updated_by',
  'price_flag.updated_date',
  'price_flag.price_difference',
  'price_flag.optimal_terminal',
  'price_flag.loaded_mile_difference',
  'price_flag.notes',
  'product_ratios',
  'suppliers',
  'po',
  'contracts',
  'load_numbers',
  'customer',
]

export const getDefaultExportConfig = (view: OrderManagementGridView) => {
  return {
    fileName: 'orders.xlsx',
    columnKeys: view === 'order' ? exportedDefaultColumns : exportedByTankColumns,
    sheetName: 'All-Orders',
    processCellCallback(params: Record<string, any>) {
      if (params.value && dateTimeColumns.includes(params.column.colId)) {
        return formatters.DATE_TIME(moment(params.value))
      }
      return params.value
    },
  }
}

const exportedOnDemandColumns = [
  'contracts',
  'number',
  'carrier',
  'flags.name',
  'market',
  'sector',
  'location_name',
  'site_name',
  'origins',
  'products',
  'carrier_start',
  'carrier_end',
  'dispatch_start',
  'dispatch_end',
  'est_drop_time',
  'bol_date',
  'delivery_date',
  'delivery_time',
  'carrier_completed',
  'product_ratios',
  'suppliers',
  'po',
  'contracts',
  'load_numbers',
  'customer',
]

const exportedByTankColumns = [
  'number',
  'product',
  'ullage',
  'volume',
  'tank_id',
  'origin',
  'destination',
  'dispatch_start',
  'dispatch_end',
  'runout',
  'dispatch_window_start_time',
]

const dateTimeColumns = [
  'carrier_start',
  'carrier_end',
  'dispatch_start',
  'dispatch_end',
  'loading_start',
  'loading_end',
  'est_drop_time',
  'carrier_completed',
  'runout',
  'dispatch_window_start_time',
]

export const getCarrierDispatchExportConfig = (view: OrderManagementGridView) => {
  return {
    fileName: 'CarrierDispatch.xlsx',
    columnKeys: view === 'order' ? exportedOnDemandColumns : exportedByTankColumns,
    sheetName: 'Carrier Dispatch',
    processCellCallback(params: Record<string, any>) {
      if (params.value && dateTimeColumns.includes(params.column.colId)) {
        return params.formatValue()
      }
      return params.value
    },
  }
}
